import assignmentSearchService, {
  searchAssignmentsFromEmail,
  quickSearchByFileId,
  searchAssignmentsMultiCriteria
} from '../../service/AssignmentSearchService';

// Helper function to map API response to assignment objects
function mapResponseToAssignments(response) {
  return response.map(item => ({
    id: item.id,
    code: item?.fileId,
    engagementType: item.engagementType,
    trackerEngagementStatus: item.trackerEngagementStatus,
    assignmentStatus: item.assignmentStatus,
    assignmentSource: item.assignmentSource, // 'active' or 'past'
    propertyName: item.propertyAddress?.propertyName,
    propertyType: item.propertyType?.name,
    primaryAppraiser: item.primaryValuer?.fullName,
    originator: item.jobOriginator?.fullName,
    client: item.client?.clientName,
    clientDetails: item.client,
    propertyAddress: item.propertyAddress,
    address: `${item.propertyAddress?.propertyName} ${item.propertyAddress?.city}`,
    workFileLink: item.workFileLink,
    viewEventId: item.viewEventId,
    draftDeliveredDate: item.draftDeliveredDate,
    sourceSystemId: item.sourceSystemId,
    clientReferenceNumber: item.clientReferenceNumber,
    objectType: item.objectType,
    deadline: {
      assignmentDueDate: item.deadline?.assignmentDueDate || item.assignmentDueDate || item.dueDate
    },
    primaryValuer: item.primaryValuer,
    primaryValuerName: item.primaryValuer?.fullName,
    primaryValuerEmail: item.primaryValuer?.emailAddress,
    originatorEmail: item.jobOriginator?.emailAddress,
    valuers: item.valuers || [],
    createdTimestamp: item.createdTimestamp,
    userFriendlyId: item.engagementId,
    subjectProperty: item.subjectProperty
  }));
}

export default {
  namespaced: true,
  state: {
    assignments: [],
    clientFollowUpAssignments: [],
    selectedAssignment: null,
    selectedAssignmentClientFollowUp: null,
    assignmentChecklist: null,
    engagementId: null,
    assignmentOverviewData: null,
    engagementOverviewDocument: null,
    // Cache of signed engagement letters keyed by engagementId
    signedEngagementLetters: {},
    isLoading: false,
    error: null,
    currentPage: 1,
    pageSize: 50,
    // New state for enhanced search
    searchResults: null,
    searchHistory: [],
    lastSearchCriteria: null,
    // Eligibility ID lists for warning banner checks
    // null = not yet fetched; [] = fetched, none eligible
    propertyFollowUpEligibleIds: null,
    clientFollowUpEligibleIds: null,
  },
  mutations: {
    SET_ASSIGNMENTS(state, assignments) {
      state.assignments = assignments
    },
    SET_CLIENT_FOLLOWUP_ASSIGNMENTS(state, assignments) {
      state.clientFollowUpAssignments = assignments
    },
    APPEND_ASSIGNMENTS(state, assignments) {
      state.assignments = [...state.assignments, ...assignments];
    },
    APPEND_CLIENT_FOLLOWUP_ASSIGNMENTS(state, assignments) {
      state.clientFollowUpAssignments = [...state.clientFollowUpAssignments, ...assignments];
    },
    SET_CURRENT_PAGE(state, page) {
      state.currentPage = page;
    },
    SET_SELECTED_ASSIGNMENT(state, assignment) {
      state.selectedAssignment = null
      state.selectedAssignment = assignment
    },
    SET_SELECTED_ASSIGNMENT_CLIENT_FOLLOWUP(state, assignment) {
      state.selectedAssignmentClientFollowUp = null
      state.selectedAssignmentClientFollowUp = assignment
    },
    CLEAR_SELECTED_ASSIGNMENT(state) {
      state.selectedAssignment = null
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_ASSIGNMENT_CHECKLIST(state, checklist) {
      state.assignmentChecklist = checklist
    },
    SET_ENGAGEMENT_ID(state, engagementId) {
      state.engagementId = engagementId
    },
    SET_ASSIGNMENT_OVERVIEW_DATA(state, data) {
      state.assignmentOverviewData = data
    },
    SET_ENGAGEMENT_OVERVIEW_DOCUMENT(state, doc) {
      state.engagementOverviewDocument = doc;
    },
    SET_SIGNED_ENGAGEMENT_LETTER(state, { engagementId, document }) {
      if (!engagementId) return;
      state.signedEngagementLetters = {
        ...state.signedEngagementLetters,
        [engagementId]: document
      };
    },
    // New mutations for enhanced search
    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },
    CLEAR_SEARCH_RESULTS(state) {
      state.searchResults = null;
    },
    SET_LAST_SEARCH_CRITERIA(state, criteria) {
      state.lastSearchCriteria = criteria;
    },
    ADD_TO_SEARCH_HISTORY(state, searchEntry) {
      state.searchHistory.unshift(searchEntry);
      // Keep only last 10 searches
      if (state.searchHistory.length > 10) {
        state.searchHistory = state.searchHistory.slice(0, 10);
      }
    },
    SET_PROPERTY_FOLLOWUP_ELIGIBLE_IDS(state, ids) {
      state.propertyFollowUpEligibleIds = ids;
    },
    SET_CLIENT_FOLLOWUP_ELIGIBLE_IDS(state, ids) {
      state.clientFollowUpEligibleIds = ids;
    },
  },
  actions: {
    /**
     * Fetch and store the raw eligible IDs for Property Follow-Up warning check.
     * Lightweight — does NOT enrich via tracker.
     */
    async loadPropertyFollowUpEligibleIds({ commit }) {
      try {
        const { fetchPropertyFollowUpEligibleIdsOnly } = await import('../../service/engagement');
        const ids = await fetchPropertyFollowUpEligibleIdsOnly();
        commit('SET_PROPERTY_FOLLOWUP_ELIGIBLE_IDS', ids);
      } catch (error) {
        // Keep as null so the backend check is skipped rather than
        // falsely marking every assignment as ineligible
        console.warn('Property follow-up eligible IDs unavailable (backend check skipped):', error.message);
        commit('SET_PROPERTY_FOLLOWUP_ELIGIBLE_IDS', null);
      }
    },

    /**
     * Fetch and store eligible IDs for Client Follow-Up warning check.
     * Reuses the existing fetchAssignmentsClientFollowUp and extracts the IDs.
     */
    async loadClientFollowUpEligibleIds({ commit }, { dateStart, dateEnd } = {}) {
      try {
        const { fetchClientFollowUpEligibleIdsOnly } = await import('../../service/engagement');
        const ids = await fetchClientFollowUpEligibleIdsOnly({ dateStart, dateEnd });
        commit('SET_CLIENT_FOLLOWUP_ELIGIBLE_IDS', ids);
      } catch (error) {
        // Keep as null so the backend check is skipped rather than
        // falsely marking every assignment as ineligible
        console.warn('Client follow-up eligible IDs unavailable (backend check skipped):', error.message);
        commit('SET_CLIENT_FOLLOWUP_ELIGIBLE_IDS', null);
      }
    },

    resetAssignments({ commit }) {
      commit('SET_ASSIGNMENTS', []);
      commit('SET_CURRENT_PAGE', 1);
    },

    async fetchAssignments({ commit }, { page = 0, pageSize = 50, reset = false, searchText }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { fetchEngagementDetails } = await import('../../service/engagement')
        const response = await fetchEngagementDetails(page, pageSize, searchText)
        const assignments = mapResponseToAssignments(response)
        if (reset) {
          commit('SET_ASSIGNMENTS', assignments);
        } else {
          commit('APPEND_ASSIGNMENTS', assignments);
        }

        // commit('SET_TOTAL_COUNT', response.total);
        commit('SET_CURRENT_PAGE', page);
        return assignments;
        // commit('SET_ASSIGNMENTS', assignments)
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error fetching assignments:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchAssignmentsClientFollowUp({ commit }, {reset = false, draftDeliveredDateStartDate = null, draftDeliveredDateEndDate = null }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { fetchEngagementDetailsClientFollowUp } = await import('../../service/engagement')
        const response = await fetchEngagementDetailsClientFollowUp(draftDeliveredDateStartDate, draftDeliveredDateEndDate)
        const assignments = mapResponseToAssignments(response)
        if (reset) {
          commit('SET_CLIENT_FOLLOWUP_ASSIGNMENTS', assignments);
        } else {
          commit('APPEND_CLIENT_FOLLOWUP_ASSIGNMENTS', assignments);
        }
        return assignments;
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error fetching assignments for client follow up:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async searchAndPrioritizeAssignment({ commit, dispatch, state }, { fileId, existingAssignments = null }) {
      if (!fileId) {
        console.warn('No fileId provided for search');
        return null;
      }

      try {
        // Search for the specific assignment
        const searchResults = await dispatch('fetchAssignments', {
          searchText: fileId.trim(),
          page: 1,
          pageSize: 50,
          reset: false
        });

        if (!searchResults || searchResults.length === 0) {
          console.log('❌ No assignment found for fileId:', fileId);
          return null;
        }

        // Find exact match or best match
        let foundAssignment = searchResults.find(assignment =>
          assignment.code && assignment.code.toLowerCase() === fileId.toLowerCase()
        ) || searchResults[0]; // Fallback to first result

        console.log('✅ Found assignment:', foundAssignment?.code, foundAssignment?.propertyName);

        // Get current assignments list (either provided or from state)
        const currentAssignments = existingAssignments || state.assignments || [];

        // Check if assignment is already in the list
        const isAlreadyInList = currentAssignments.some(assignment =>
          assignment.id === foundAssignment.id
        );

        let updatedAssignments;

        if (isAlreadyInList) {
          // Move existing assignment to top
          updatedAssignments = [
            foundAssignment,
            ...currentAssignments.filter(assignment => assignment.id !== foundAssignment.id)
          ];
        } else {
          // Add new assignment to top
          updatedAssignments = [foundAssignment, ...currentAssignments];
        }

        // Update state with prioritized list
        commit('SET_ASSIGNMENTS', updatedAssignments);

        return foundAssignment;

      } catch (error) {
        console.error('Error searching and prioritizing assignment:', error);
        return null;
      }
    },

    /**
     * Enhanced hierarchical search with multiple fallback strategies
     */
    async searchAssignmentsEnhanced({ commit, dispatch, state }, searchOptions = {}) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        console.log('🔝 Starting enhanced assignment search:', searchOptions);

        const {
          fileId,
          assignmentId,
          propertyName,
          propertyAddress,
          clientName,
          emailData,
          autoExtractFromEmail = true,
          maxResults = 50,
          enableCache = true,
          fallbackToFuzzy = true
        } = searchOptions;

        // Build search criteria
        let searchCriteria = {};

        // Primary criteria from parameters
        if (fileId) searchCriteria.fileId = fileId;
        if (assignmentId) searchCriteria.assignmentId = assignmentId;
        if (propertyName) searchCriteria.propertyName = propertyName;
        if (propertyAddress) searchCriteria.propertyAddress = propertyAddress;
        if (clientName) searchCriteria.clientName = clientName;

        // Auto-extract criteria from email if enabled and available
        if (autoExtractFromEmail && emailData) {
          const emailSearchResult = await searchAssignmentsFromEmail(emailData, {
            maxResults: 10,
            enableCache,
            includeScoring: true
          });

          if (emailSearchResult.assignments.length > 0) {
            console.log('📧 Email extraction found assignments:', emailSearchResult.assignments.length);

            // Merge email-extracted assignments with current list
            const currentAssignments = state.assignments || [];
            const mergedAssignments = this.mergeAndPrioritizeAssignments(
              emailSearchResult.assignments,
              currentAssignments
            );

            commit('SET_ASSIGNMENTS', mergedAssignments);
            commit('SET_SEARCH_RESULTS', emailSearchResult);
            commit('SET_LAST_SEARCH_CRITERIA', { emailData: true });

            return {
              success: true,
              assignments: emailSearchResult.assignments,
              strategy: emailSearchResult.searchStrategy,
              source: 'email'
            };
          }
        }

        // If no email results or email extraction disabled, proceed with hierarchical search
        if (Object.keys(searchCriteria).length === 0) {
          console.warn('❌ No search criteria provided');
          return { success: false, assignments: [], message: 'No search criteria provided' };
        }

        // Execute hierarchical search
        const searchResult = await assignmentSearchService.searchAssignments(searchCriteria, {
          maxResults,
          enableCache,
          fallbackToFuzzy,
          includeScoring: true
        });

        // Store search results
        commit('SET_SEARCH_RESULTS', searchResult);
        commit('SET_LAST_SEARCH_CRITERIA', searchCriteria);
        commit('ADD_TO_SEARCH_HISTORY', {
          criteria: searchCriteria,
          timestamp: new Date().toISOString(),
          resultCount: searchResult.assignments.length,
          strategy: searchResult.searchStrategy
        });

        if (searchResult.assignments.length > 0) {
          console.log(`✅ Enhanced search found ${searchResult.assignments.length} assignments using ${searchResult.searchStrategy} strategy`);

          // Merge with existing assignments and prioritize
          const currentAssignments = state.assignments || [];
          const mergedAssignments = this.mergeAndPrioritizeAssignments(
            searchResult.assignments,
            currentAssignments
          );

          commit('SET_ASSIGNMENTS', mergedAssignments);

          return {
            success: true,
            assignments: searchResult.assignments,
            strategy: searchResult.searchStrategy,
            score: searchResult.score,
            searchTime: searchResult.searchTime
          };
        } else {
          console.log('❌ Enhanced search found no assignments');
          return {
            success: false,
            assignments: [],
            strategy: searchResult.searchStrategy,
            message: 'No assignments found matching the search criteria'
          };
        }

      } catch (error) {
        console.error('💥 Enhanced search failed:', error);
        commit('SET_ERROR', error.message);
        return {
          success: false,
          assignments: [],
          error: error.message
        };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Quick search by File ID with enhanced fallback
     */
    async quickSearchByFileId({ commit, dispatch }, { fileId, enableFallback = true }) {
      if (!fileId) {
        console.warn('No fileId provided for quick search');
        return { success: false, message: 'File ID is required' };
      }

      try {
        console.log('⚡ Quick search for File ID:', fileId);

        const result = await quickSearchByFileId(fileId, {
          maxResults: 20,
          includeScoring: true
        });

        if (result.assignments.length > 0) {
          console.log(`✅ Quick search found ${result.assignments.length} assignments`);
          return {
            success: true,
            assignments: result.assignments,
            strategy: result.searchStrategy
          };
        } else if (enableFallback) {
          console.log('🔄 Quick search failed, trying enhanced search...');
          return await dispatch('searchAssignmentsEnhanced', { fileId });
        } else {
          return { success: false, assignments: [], message: 'No assignments found for File ID' };
        }

      } catch (error) {
        console.error('Error in quick search:', error);
        return { success: false, error: error.message };
      }
    },

    /**
     * Search from current email context
     */
    async searchFromCurrentEmail({ commit, dispatch }) {
      try {
        console.log('📧 Extracting assignment info from current email...');

        // Get current email data from Office.js
        const { getCurrentEmailData } = await import('../../utils/emailAssignmentExtractor');
        const emailData = await getCurrentEmailData();

        if (!emailData) {
          console.warn('No email data available');
          return { success: false, message: 'No email data available' };
        }

        return await dispatch('searchAssignmentsEnhanced', {
          emailData,
          autoExtractFromEmail: true,
          maxResults: 20
        });

      } catch (error) {
        console.error('Error searching from current email:', error);
        return { success: false, error: error.message };
      }
    },

    /**
     * Helper method to merge and prioritize assignments
     */
    mergeAndPrioritizeAssignments(newAssignments, existingAssignments) {
      const merged = [...newAssignments];
      const newIds = new Set(newAssignments.map(a => a.id));

      // Add existing assignments that aren't already in new results
      existingAssignments.forEach(assignment => {
        if (!newIds.has(assignment.id)) {
          merged.push(assignment);
        }
      });

      // Sort by search score if available, then by created timestamp
      return merged.sort((a, b) => {
        if (a.searchScore !== undefined && b.searchScore !== undefined) {
          return b.searchScore - a.searchScore;
        }

        const dateA = new Date(a.createdTimestamp || 0);
        const dateB = new Date(b.createdTimestamp || 0);
        return dateB - dateA;
      });
    },

    /**
     * Clear search results and reset
     */
    async clearSearchResults({ commit }) {
      commit('CLEAR_SEARCH_RESULTS');
      commit('SET_LAST_SEARCH_CRITERIA', null);
      assignmentSearchService.clearCache();
    },

    async selectAssignment({ commit, dispatch }, assignment) {
      commit('SET_SELECTED_ASSIGNMENT', assignment)

      // Preload assignment overview data when assignment is selected
      if (assignment && assignment.viewEventId) {
        try {
          await dispatch('fetchAssignmentOverviewData', assignment.viewEventId);
        } catch (error) {
          console.error('Error preloading assignment overview data:', error);
          // Don't throw error here to avoid blocking assignment selection
        }
      }

      // Fetch engagement overview document if engagementId available in assignmentOverviewData
      if (assignment && assignment.sourceSystemId) {
        try {
          await dispatch('fetchEngagementOverviewDocument', { engagementId: assignment.sourceSystemId });
        } catch (error) {
          console.error('Error fetching engagement overview document:', error);
        }
      }
    },
     async selectAssignmentClientFollowUp({ commit, dispatch }, assignment) {
      commit('SET_SELECTED_ASSIGNMENT_CLIENT_FOLLOWUP', assignment)
    },
    setAssignmentChecklist({ commit }, checklist) {
      commit('SET_ASSIGNMENT_CHECKLIST', checklist)
    },
    setEngagementId({ commit }, engagementId) {
      commit('SET_ENGAGEMENT_ID', engagementId)
    },
    async fetchAssignmentOverviewData({ commit, state }, assignmentId) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { fetchAssignmentOverviewData } = await import('../../service/engagement')
        const data = await fetchAssignmentOverviewData(assignmentId);

        commit('SET_ASSIGNMENT_OVERVIEW_DATA', data);

        // Extract engagement ID if available in the response
        if (data.engagementId) {
          commit('SET_ENGAGEMENT_ID', data.engagementId);
        }

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        console.error('Error fetching assignment overview data:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchEngagementOverviewDocument({ state, commit }, { engagementId, workfileId = null } = {}) {
      // Handle backward compatibility - if first param is string, treat as engagementId
      if (typeof arguments[1] === 'string') {
        engagementId = arguments[1];
        workfileId = null;
      }

      commit('SET_ERROR', null);
      if (!engagementId) {
        const err = 'engagementId is required';
        commit('SET_ERROR', err);
        return null;
      }
      try {
        // Return cached version if present
        if (state.signedEngagementLetters[engagementId]) {
          return state.signedEngagementLetters[engagementId];
        }
        console.log('Fetching engagement letter for', engagementId);
        const { fetchEngagementOverviewDocument } = await import('../../service/engagement');
        const doc = await fetchEngagementOverviewDocument(engagementId, workfileId);
        commit('SET_ENGAGEMENT_OVERVIEW_DOCUMENT', doc);
        commit('SET_SIGNED_ENGAGEMENT_LETTER', { engagementId, document: doc });
        return doc;
      } catch (error) {
        commit('SET_ERROR', error.message);
        console.error('Error fetching engagement overview document:', error);
        throw error;
      }
    },
    async fetchDataRequest({ commit, state }, userFriendlyId) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { fetchDataRequest } = await import('../../service/engagement')
        const data = await fetchDataRequest(userFriendlyId);

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        console.error('Error fetching data request:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async uploadFiles({ commit }, payload) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { uploadFileToFolder } = await import('../../service/engagement')
        const response = await uploadFileToFolder(payload.folderId, payload.multiPartFormData, payload.config)

        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error uploading files:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateAssignmentDueDate({ commit }, payload) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { changeAssignmentDueDate } = await import('../../service/engagement')
        const response = await changeAssignmentDueDate(payload)

        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error updating assignment due date: ', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async addProofOfDeliveryItem({ commit }, payload) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { addProofOfDelivery } = await import('../../service/engagement')
        const response = await addProofOfDelivery(payload)

        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error adding proof of delivery item: ', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateDeliveryDate({ commit }, payload) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { updateDeliveryDate } = await import('../../service/engagement')
        const response = await updateDeliveryDate(payload)

        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error updating delivery date: ', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchAssignmentChecklist({ commit }, eventId) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { getAssignmentChecklist } = await import('../../service/engagement')
        const response = await getAssignmentChecklist(eventId)
        if (response && response.checklist)
          commit('SET_ASSIGNMENT_CHECKLIST', response.checklist)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error fetching assignment checklist: ', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async addMetricsData({ commit }, payload) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const { addMetrics } = await import('../../service/engagement')
        const response = await addMetrics(payload)

        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        console.error('Error adding metrics data: ', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateSelectedAssignmentDueDate({ commit, state }, newDueDate) {
      try {
        // Update the selected assignment's due date in the store
        if (state.selectedAssignment && state.selectedAssignment.deadline) {
          const updatedAssignment = {
            ...state.selectedAssignment,
            deadline: {
              ...state.selectedAssignment.deadline,
              assignmentDueDate: newDueDate
            }
          };
          commit('SET_SELECTED_ASSIGNMENT', updatedAssignment);
          console.log('✅ Selected assignment due date updated in store');
        }
      } catch (error) {
        console.error('Error updating selected assignment due date in store:', error);
        throw error;
      }
    },
  },
  getters: {
    getAssignments: state => state.assignments,
    getClientFollowUpAssignments: state => state.clientFollowUpAssignments,
    selectedAssignment: state => state.selectedAssignment,
    selectedAssignmentClientFollowUp: state => state.selectedAssignmentClientFollowUp,
    assignmentChecklist: state => state.assignmentChecklist,
    engagementId: state => state.engagementId,
    assignmentOverviewData: state => state.assignmentOverviewData,
    engagementOverviewDocument: state => state.engagementOverviewDocument,
    getSignedEngagementLetterById: state => engagementId => state.signedEngagementLetters[engagementId],
    isLoading: state => state.isLoading,
    error: state => state.error,

    // New getters for enhanced search
    searchResults: state => state.searchResults,
    searchHistory: state => state.searchHistory,
    lastSearchCriteria: state => state.lastSearchCriteria,
    hasSearchResults: state => state.searchResults && state.searchResults.assignments.length > 0,
    searchPerformanceMetrics: () => assignmentSearchService.getPerformanceMetrics(),

    // Getter for assignments with search scoring
    assignmentsWithScores: state => {
      return state.assignments.map(assignment => ({
        ...assignment,
        hasSearchScore: assignment.searchScore !== undefined,
        displayScore: assignment.searchScore ? Math.round(assignment.searchScore * 100) : null
      }));
    },

    // Get top scored assignments
    topScoredAssignments: state => {
      return state.assignments
        .filter(a => a.searchScore !== undefined)
        .sort((a, b) => b.searchScore - a.searchScore)
        .slice(0, 5);
    },

    // ── Eligibility: Property Follow Up ──────────────────────────────────────
    // Returns true (eligible), false (ineligible), or null (no assignment / IDs not yet fetched)
    isEligibleForPropertyFollowUp: state => {
      const assignment = state.selectedAssignment;
      if (!assignment) return null;

      // Status check: Billing (5) or Completed (6) → ineligible
      const statusId = assignment.trackerEngagementStatus?.id;
      if (statusId === 5 || statusId === 6) return false;

      // Backend ID check: only run once the ID list has been fetched (non-null)
      if (state.propertyFollowUpEligibleIds !== null) {
        const candidates = [
          String(assignment.code || ''),
          String(assignment.id || ''),
          String(assignment.viewEventId || ''),
        ].filter(Boolean);
        const matched = state.propertyFollowUpEligibleIds.some(id => candidates.includes(String(id)));
        if (!matched) return false;
      }

      return true;
    },

    propertyFollowUpIneligibilityReason: state => {
      const assignment = state.selectedAssignment;
      if (!assignment) return null;
      const statusId = assignment.trackerEngagementStatus?.id;
      const statusName = assignment.trackerEngagementStatus?.name || 'Unknown';
      if (statusId === 5)
        return `Assignment completed (in billing).`;
      if (statusId === 6)
        return `Assignment completed.`;

      // Not found in the backend eligible list
      if (state.propertyFollowUpEligibleIds !== null) {
        const candidates = [
          String(assignment.code || ''),
          String(assignment.id || ''),
          String(assignment.viewEventId || ''),
        ].filter(Boolean);
        const matched = state.propertyFollowUpEligibleIds.some(id => candidates.includes(String(id)));
        if (!matched)
          return 'No Property Contact Request has been sent for this assignment.';
      }

      return null;
    }
  }
}

